import { MAIL_ITEMS, GALLERY } from "@/lib/brand";
import { billingLine, inr } from "@/lib/plans";

export const Firefly = ({ className = "" }) => (
  <span className={`inline-block h-2 w-2 rounded-full bg-amber-300 shadow-[0_0_12px_4px_rgba(250,204,21,0.55)] ${className}`} />
);

export const MailGallery = () => (
  <section className="py-24" data-testid="plan-inside-section">
    <div className="max-w-6xl mx-auto px-6">
      <p className="font-hand text-3xl text-amber-700 -rotate-1">open it slowly</p>
      <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone-900 mt-2">What comes inside your mail?</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {MAIL_ITEMS.map((it, i) => (
          <figure key={it.key} className={`group bg-[#FFFDF9] border border-stone-200 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`} data-testid={`plan-inside-item-${it.key}`}>
            <div className="overflow-hidden"><img src={it.img} alt={it.title} loading="lazy" className={`w-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ${i === 0 ? "aspect-square lg:aspect-[4/3.6]" : "aspect-square"}`} /></div>
            <figcaption className="p-5">
              <p className="font-serif-display text-2xl text-stone-900">{it.title}</p>
              <p className="text-stone-600 text-sm mt-1 leading-relaxed">{it.text}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  "Choose your subscription duration.",
  "Receive your Little Lantern Club snail mail at the applicable delivery intervals.",
  "Open your mail, explore the activities, create something and enjoy a little moment for yourself.",
];

export const HowItWorks = () => (
  <section className="py-24 bg-stone-900 text-stone-100 relative overflow-hidden" data-testid="plan-how-section">
    <div className="lantern-glow absolute w-[30rem] h-[30rem] rounded-full -left-40 -bottom-40 opacity-50" />
    <div className="max-w-6xl mx-auto px-6 relative">
      <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl">How does it work?</h2>
      <ol className="grid md:grid-cols-3 gap-10 mt-14">
        {STEPS.map((s, i) => (
          <li key={s} className="border-t border-stone-700 pt-6" data-testid={`plan-how-step-${i + 1}`}>
            <span className="font-hand text-5xl text-amber-300">{i + 1}</span>
            <p className="mt-3 text-lg text-stone-200 leading-relaxed">{s}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Row = ({ label, value, id }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-4 border-b border-stone-200 last:border-0">
    <dt className="text-stone-500">{label}</dt>
    <dd className="text-stone-900 font-medium sm:text-right" data-testid={`plan-detail-${id}`}>{value}</dd>
  </div>
);

export const PlanFacts = ({ plan }) => (
  <section className="py-24 bg-[#FAF7F2]" data-testid="plan-details-section">
    <div className="max-w-3xl mx-auto px-6">
      <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900">{plan.recurring ? "Your subscription, in detail" : "Your envelope, in detail"}</h2>
      <dl className="mt-10 bg-white border border-stone-200 rounded-3xl px-7 py-2">
        <Row id="plan" label="Selected plan" value={plan.recurring ? `${plan.name} (${plan.durationLabel})` : plan.name} />
        <Row id="price" label="Total price" value={inr(plan.price)} />
        <Row id="duration" label="Duration" value={plan.recurring ? plan.durationLabel : "A single envelope"} />
        <Row id="frequency" label="Delivery frequency" value={plan.recurring ? "One envelope every month" : "Posted once"} />
        <Row id="packages" label="Envelopes in this period" value={plan.months === 1 ? "1 envelope" : `${plan.months} envelopes`} />
        <Row id="billing" label="Billing" value={plan.recurring ? `${billingLine(plan)}, auto-renewing via Razorpay` : `${billingLine(plan)}, paid via Razorpay`} />
        <Row id="shipping" label="Shipping" value="Free, within India only" />
      </dl>
      {plan.months > 1 && (
        <p className="text-stone-600 mt-6 leading-relaxed" data-testid="plan-longer-explainer">
          You pay {inr(plan.price)} once for {plan.durationLabel}, and we post one envelope to you each month, {plan.months} in total over the period. When the {plan.durationLabel} are up, your subscription renews automatically on Razorpay.
        </p>
      )}
    </div>
  </section>
);

export const PhotoGallery = () => (
  <section className="py-24" data-testid="plan-gallery-section">
    <div className="max-w-6xl mx-auto px-6">
      <p className="font-hand text-3xl text-amber-700 -rotate-1">a closer look</p>
      <div className="columns-1 sm:columns-2 gap-6 mt-8 [&>*]:mb-6">
        {GALLERY.map((g) => (
          <img key={g.src} src={g.src} alt={g.alt} loading="lazy" className={`w-full rounded-2xl object-cover break-inside-avoid ${g.tall ? "aspect-[2/3]" : "aspect-[3/2]"}`} />
        ))}
      </div>
    </div>
  </section>
);
